import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { AnimatedCounter } from "@/components/AnimatedCounter";
import { InfoTip } from "@/components/InfoTip";
import { ArrowDownRight, Instagram, Mail } from "lucide-react";
import { HeroVisual } from "@/components/HeroVisual";
import { SOCIAL } from "@/data/sponsors";

const fade = (d) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, delay: d, ease: [0.22, 1, 0.36, 1] },
});

export const Hero = () => (
  <section
    id="top"
    className="grain relative overflow-hidden pt-36 sm:pt-40"
    data-testid="hero-section"
  >
    <div className="pointer-events-none absolute -left-40 top-10 h-[420px] w-[420px] rounded-full bg-[#E0708A]/20 blur-[120px] di-blob" />
    <div className="pointer-events-none absolute -right-32 bottom-0 h-[380px] w-[380px] rounded-full bg-[#5E2A52]/15 blur-[130px] di-blob" />

    <div className="relative mx-auto grid max-w-[1400px] items-center gap-6 px-5 pb-14 sm:px-10 lg:grid-cols-[1.05fr_0.95fr] lg:pb-24">
      <div>
        <motion.div
          {...fade(0)}
          className="mb-7 inline-flex items-center gap-2 rounded-full border border-[#E8E2E4] bg-white/70 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-[#5E2A52] backdrop-blur-xl"
          data-testid="hero-badge"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-[#E0708A]" />
          Donna Impresa · Rete di donne in impresa · Tutta Italia
        </motion.div>

        <motion.h1
          {...fade(0.08)}
          className="font-display text-[2.6rem] leading-[1] tracking-tight text-[#1A1617] sm:text-6xl sm:leading-[0.95] lg:text-7xl"
          data-testid="hero-title"
        >
          Hai messo in piedi
          <br />
          la tua impresa.
          <br />
          <span className="di-shine italic">Raccontacela</span>.
        </motion.h1>

        <motion.p
          {...fade(0.16)}
          className="mt-7 max-w-xl text-base leading-relaxed text-[#6B5E62]"
          data-testid="hero-subtitle"
        >
          <strong className="font-semibold text-[#1A1617]">DonnaImpresa</strong>{" "}
          è la rete delle donne in impresa in Italia: raccogliamo e pubblichiamo
          le storie delle donne imprenditrici, per farvi conoscere e per farvi
          incontrare. Mandaci la tua in due minuti: è gratuito e la pubblichiamo
          solo se sei d'accordo.
        </motion.p>

        <motion.div {...fade(0.24)} className="mt-10 flex flex-wrap items-center gap-4">
          <Link
            to="/racconta-la-tua-storia"
            data-testid="hero-cta-primary"
            className="group inline-flex items-center gap-2 rounded-full bg-[#5E2A52] px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-[#E0708A]"
          >
            Racconta la tua storia
            <ArrowDownRight
              size={17}
              className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:translate-y-0.5"
            />
          </Link>
          <div className="flex items-center gap-3">
            <a
              href={SOCIAL.instagram}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-instagram-link"
              className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-[#E8E2E4] bg-white text-[#5E2A52] transition-colors duration-300 hover:border-[#E0708A] hover:text-[#E0708A]"
            >
              <Instagram size={19} />
            </a>
            <a
              href="mailto:impresahub@gmail.com"
              data-testid="hero-email-link"
              className="inline-flex items-center gap-2 text-sm text-[#6B5E62] transition-colors duration-300 hover:text-[#E0708A]"
            >
              <Mail size={16} className="text-[#E0708A]" />
              impresahub@gmail.com
            </a>
          </div>
        </motion.div>

        <motion.p
          {...fade(0.28)}
          className="mt-4 text-xs text-[#6B5E62]"
          data-testid="hero-reassurance"
        >
          Due minuti · gratuito · nessun impegno · pubblichiamo solo con il tuo ok
        </motion.p>

        <motion.dl
          {...fade(0.32)}
          className="mt-12 grid grid-cols-2 gap-x-5 gap-y-6 sm:mt-14 sm:flex sm:flex-wrap sm:gap-x-10"
          data-testid="hero-stats"
        >
          {[
            [120, "+", "Imprenditrici", "Professioniste e titolari d'impresa che fanno già parte della rete."],
            [2, " min", "Per raccontarti", "Il modulo è breve: chi sei, cosa fai e com'è iniziato tutto."],
            [0, " €", "Costo", "Partecipare alla rete e raccontare la tua storia è gratuito."],
            [100, "%", "Decidi tu", "Pubblichiamo la tua storia solo dopo la tua approvazione."],
          ].map(([n, suffix, l, tip]) => (
            <div key={l} data-testid={`hero-stat-${l.toLowerCase().split(" ")[0]}`}>
              <dt className="font-display text-2xl text-[#5E2A52] sm:text-3xl">
                <AnimatedCounter value={n} suffix={suffix} />
              </dt>
              <dd className="mt-1 flex items-start gap-1 text-[10px] uppercase leading-tight tracking-[0.12em] text-[#6B5E62] sm:text-xs sm:tracking-[0.15em]">
                {l}
                <InfoTip text={tip} testId={`tip-stat-${l.toLowerCase().split(" ")[0]}`} />
              </dd>
            </div>
          ))}
        </motion.dl>
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
      >
        <HeroVisual />
      </motion.div>
    </div>
  </section>
);
